import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { apiService, fetchingAPI } from '@/api'

// Module-level promise cache keyed by organizationId. The Call Explorer can
// mount several notes surfaces at once (Comments aggregate, scorecard note,
// criteria note), and each used to fetch the full org user list on mount.
// De-dupe so concurrent/sequential mounts share one request — and one
// resolved array, which also lets renderMentions' WeakMap regex cache hit
// across every surface. Org membership is treated as stable for the session;
// a failed fetch evicts its entry so a later mount can retry.
const orgUsersCache = new Map()

const loadOrgUsers = (organizationId, dispatch) => {
  if (orgUsersCache.has(organizationId)) {
    return orgUsersCache.get(organizationId)
  }
  const promise = fetchingAPI(
    `${apiService.core}/api/organizations/${organizationId}/users`,
    'GET',
    dispatch
  )
    .then((response) => (Array.isArray(response?.users) ? response.users : []))
    .catch(() => {
      orgUsersCache.delete(organizationId)
      return []
    })
  orgUsersCache.set(organizationId, promise)
  return promise
}

/**
 * Fetch-once-per-session hook for an org's user list. Shared between the
 * NoteForm (autocomplete picker) and NoteCard (mention highlighting).
 */
export const useOrgUsers = (organizationId) => {
  const dispatch = useDispatch()
  const [users, setUsers] = useState([])

  useEffect(() => {
    let cancelled = false
    if (organizationId) {
      loadOrgUsers(organizationId, dispatch).then((list) => {
        if (!cancelled) {
          setUsers(list)
        }
      })
    }
    return () => {
      cancelled = true
    }
  }, [organizationId, dispatch])

  return users
}

export const fullNameOf = (user) =>
  `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || ''
